<script setup>
import { usePlaylistStore } from "@/stores/playlist.js";
import { enableScroll, disableScroll, scrollToSlide  } from "@/Helpers/scroll";
import { ref, onMounted, watch, getCurrentInstance, computed } from 'vue';

const props = defineProps({
    row: {
        type: Object,
        default: undefined,
    },
    playlistId: {
        type: Number,
        default: undefined,
    },
    isIntersecting: Boolean,
    scormExport: Boolean,
})
const store = usePlaylistStore();
const instance = getCurrentInstance();
const isAnswered = ref(false);
const componentID = 'a'+Math.random().toString(36).slice(2, 7);
const currentStep = ref(1);
const scrollContainer = ref(null);
const firstPathSegment = window.location.pathname.split('/')[1];

const errors = ref([]);

const totalSteps = computed(() => {
    return props.row && props.row.content && props.row.content.pages
        ? props.row.content.pages.length
        : 1;
});

function buttonPressed() {
    if (currentStep.value === totalSteps.value) {
        if (checkPageAnswers() === false) {
            return;
        }

        isAnswered.value = true;
        enableScroll();
        saveResponse(
            `Completed Multi Page Form - ${props.row.content.pages.map(page => page.title).join(', ')}`,
            'Completed',
            true
        );
    } else {
        goToNextPage();
    }
}

function goToNextPage() {
    if (checkPageAnswers() === false) {
        return;
    }

    if (currentStep.value < totalSteps.value && scrollContainer.value) {
        currentStep.value++;
        const pageWidth = scrollContainer.value.offsetWidth;
        scrollContainer.value.scrollTo({
            left: pageWidth * (currentStep.value - 1),
            behavior: 'smooth'
        });
    }
}

const scormApi = ref(null);
if (props.scormExport) {
    scormApi.value = instance.appContext.config.globalProperties.$scorm.api;
}

watch(
    () => props.isIntersecting,
    (newVal, oldVal) => { 
        if (props.isIntersecting === true && isAnswered.value === false) {
            disableScroll();
        } else if (props.isIntersecting === true && isAnswered.value === true) {
            enableScroll();
        }
        if (newVal === false && oldVal === true) {
            enableScroll();
        }
    }
);

function setPreviousAnswer() {
    // Forms are intentionally not pre-filled with previous answers
}

onMounted(() => {
    setPreviousAnswer()
});

function checkPageAnswers() {
    errors.value = [];
    const currentPage = props.row.content.pages[currentStep.value - 1];
    let pageValid = true;
    currentPage.fields.forEach((field, index) => {
        if (field.required === true || field.required === 'true') {
            const inputElem = scrollContainer.value.querySelectorAll('input, select')[index + ((currentStep.value - 1) * currentPage.fields.length)];
            if (inputElem) {
                if (inputElem.value === '' || inputElem.value === null) {
                    errors.value[index] = 'This field is required.';
                    pageValid = false;
                } else {
                    errors.value[index] = null;
                }
            }
        }
    });

    return pageValid;
}

function saveResponse() {
    if (firstPathSegment === 'preview' || firstPathSegment === 'admin') {
        scrollToSlide(store.currentSlideIndex + 1);
        return;
    };

    // iterate through pages and fields to build data array
    for (let i = 0; i < props.row.content.pages.length; i++) {
        const page = props.row.content.pages[i];
        for (let j = 0; j < page.fields.length; j++) {
            const field = page.fields[j];
            const inputElem = scrollContainer.value.querySelector('#' + componentID + '-' + i + '-' + j);
            if (inputElem) {
                const answer = inputElem.value;
                const question = `${field.label}`;
                const data = {
                    playlist_id: props.playlistId,
                    playlist_card_row_id: props.row.id,
                    type: 'Form',
                    question: question,
                    answer: answer,
                    isCorrect: true,
                };

                if (props.scormExport && scormApi.value) {
                    scormApi.value.recordInteraction(data);
                } else {
                    const url = '/activity/store_response'
                    axios.post(url, data)
                        .then(function (response) {
                            // do nothing
                        })
                        .catch(function (error) {
                            // do nothing
                        });
                }
            }
        }
    }
    enableScroll();
    scrollToSlide(store.currentSlideIndex + 1);
}
</script>
<template>
    <div :id="componentID" class="relative p-fl-4  w-full h-full overflow-hidden card-text">
        <!-- Scroll container -->
        <div
            ref="scrollContainer"
            class="flex h-full w-full scroll-smooth overflow-x-hidden"
        >
            <!-- Each page -->
            <div
                v-for="(page, pageIndex) in row.content.pages"
                :key="'mpf-page-' + pageIndex"
                class="flex flex-col gap-2 justify-between h-full w-full shrink-0 p-fl-4 pb-fl-2"
            >
                <div class="text-fl-3xl font-bold">
                    {{ page.title }}
                </div>
                <div
                    v-if="totalSteps > 1"
                    class="flex items-center justify-between gap-2 w-full"
                >
                    <div class="grid flex-1 gap-1 step-container" :style="{ gridTemplateColumns: `repeat(${totalSteps}, 1fr)` }">
                        <div
                            v-for="step in totalSteps"
                            :key="'mpf-step-' + step"
                            class="h-2 step"
                            :class="step <= currentStep ? 'active' : 'inactive'"
                        />
                    </div>
                    <div class="shrink-0 brandText">
                        {{ currentStep }} of {{ totalSteps }}
                    </div>
                </div>
                <div class="flex-1 flex flex-col gap-4">
                    <div
                        v-for="(field, fieldIndex) in page.fields"
                        :key="'mpf-field-' + fieldIndex"
                    >
                        <label class="font-semibold text-fl-base">{{ field.label }}{{ field.required ? '*' : '' }}:</label>
                        <input
                            v-if="field.type === 'text'"
                            :id="componentID + '-' + pageIndex + '-' + fieldIndex"
                            type="text"
                            :placeholder="field.placeholder"
                            class="w-full mt-1 input-border text-black bg-white"
                            :required="field.required"
                        >
                        <select
                            v-else-if="field.type === 'dropdown'"
                            :id="componentID + '-' + pageIndex + '-' + fieldIndex"
                            class="w-full mt-1 input-border text-black bg-white"
                            :required="field.required"
                        >
                            <option value="" disabled selected>
                                Select
                            </option>
                            <option
                                v-for="(option, optionIndex) in field.options"
                                :key="'mpf-option-' + optionIndex"
                                :value="option"
                            >
                                {{ option }}
                            </option>
                        </select>
                        <p v-if="errors[fieldIndex]" class="text-red-600 mt-1">
                            {{ errors[fieldIndex] }}
                        </p>
                    </div>
                </div>
                <div class="shrink-0 w-full">
                    <button
                        type="button"
                        class="rounded-full w-full font-bold shadow-sm border-2 py-fl-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white transition-all"
                        @click="buttonPressed"
                    >
                        {{ (pageIndex + 1) !== totalSteps ? 'Next' : 'Submit' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
button {
    border-color: var(--button_bg);
    background-color: var(--button_bg);
    color: var(--button_text);
}

button:hover {
    background-color: var(--button_hover_bg);
    color: var(--button_hover_text);
}

.brandText {
    color: var(--button_bg);
}

.cardText {
    color: var(--card_text);
}

.input-border {
    border: 2px solid var(--card-text);
    border-radius: 5px;
    padding: 0.5rem;
}

.step:nth-child(1) {
    border-radius: 5px 0 0% 5px;
}

.step:nth-last-child(1) {
    border-radius: 0 5px 5px 0;
}

.step.active {
    background-color: var(--button_bg);
}

.step.inactive {
    background-color: color-mix(
        in srgb,
        var(--button_bg) 30%,
        transparent
    );
}
</style>